import {
  Divider,
  Fade,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  MenuProps,
  PaperProps,
  Popover,
  useTheme,
} from "@mui/material";
import React, { useEffect } from "react";

export type PopoverMenuItem =
  | {
      disabled?: boolean;
      href?: string;
      icon?: React.ReactNode;
      id?: string;
      label: string;
      onClick?: () => void;
      target?: string;
      type?: "item";
    }
  | {
      id?: string;
      type: "divider";
    };

interface PopoverMenuProps extends Omit<MenuProps, "open"> {
  items: PopoverMenuItem[];
  open: boolean; // Explicitly required
}

export const PopoverMenu: React.FC<PopoverMenuProps> = ({
  items,
  onClose,
  slotProps,
  ...menuProps
}) => {
  const theme = useTheme();

  useEffect(() => {
    const handleResize = () => {
      if (onClose && menuProps.open) {
        onClose({}, "backdropClick");
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [onClose, menuProps.open]);

  const handleItemClick = (
    event: React.MouseEvent<HTMLLIElement>,
    item: Extract<PopoverMenuItem, { type?: "item" }>,
  ) => {
    if (item.onClick) {
      item.onClick();
    }
    if (onClose) {
      onClose(event, "backdropClick");
    }
  };

  return (
    <Popover
      TransitionComponent={Fade}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      autoFocus={false}
      disableScrollLock={true}
      onClose={onClose}
      slotProps={{
        root: slotProps?.root,
        paper: {
          elevation: 3,
          ...slotProps?.paper,
          sx: {
            mt: 1.5,
            width: 305,
            maxWidth: "100%",
            borderRadius: 2,
            p: 1,
            "& .MuiList-root": {
              padding: 0,
            },
            "& .MuiMenuItem-root": {
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              color: theme.colors.primary[5],
            },
            "& .MuiListItemText-primary": {
              fontFamily: theme.typography.fontFamily,
              fontWeight: 600,
              fontSize: "14px",
            },
            "& .MuiListItemIcon-root": {
              color: theme.colors.primary[5],
            },
            ...((slotProps?.paper as PaperProps | undefined)?.sx ?? {}),
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      {...menuProps}
    >
      <MenuList>
        {items.map((item, index) => {
          if (item.type === "divider") {
            return (
              <Divider key={item.id ?? `divider-${index}`} sx={{ my: 1 }} />
            );
          }

          const isLink = !!item.href;

          return (
            <MenuItem
              component={isLink ? "a" : "li"}
              disabled={item.disabled}
              key={item.id ?? item.label}
              onClick={(e: React.MouseEvent<HTMLLIElement>) =>
                handleItemClick(e, item)
              }
              {...(isLink
                ? {
                    href: item.href,
                    target: item.target ?? "_blank",
                    rel: "noopener noreferrer",
                  }
                : {})}
            >
              <ListItemText primary={item.label} />
              {item.icon && (
                <ListItemIcon sx={{ minWidth: "auto !important" }}>
                  {item.icon}
                </ListItemIcon>
              )}
            </MenuItem>
          );
        })}
      </MenuList>
    </Popover>
  );
};
